import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { DELIVERY_RESTRICTION_CHOICE, DELIVERY_RESTRICTION_CUSTOM, GOOGLE_API_STATUS } from '@/constants/enum';
import { handleSendMessageCrisp, isEmpty, openToast } from '@/helpers';
import { StyledPadding } from '@/pages/GeneralSetting/styled';
import { useGetGeneralSettingQuery, useLazyCheckGoogleRoutesApiKeyQuery, useUpdateGeneralSettingMutation } from '@/redux/query';
import {
  handleVisibleBanner,
  isOpenAddPostalCodeBannerSelector,
  isOpenCustomRadiusBannerSelector,
  isOpenNoValidPostalCodeBannerSelector,
} from '@/redux/slice/banner.slice';
import { errorFieldsSelector, handleResetErrorFields, handleSetErrorFields } from '@/redux/slice/error.slice';
import { handleVisibleGGApiBanner, showBannerGGApiSelector } from '@/redux/slice/ggApiBanner.slice';
import {
  generalBackupSettingSelector,
  generalSettingSelector,
  handleSaveBackupSettings,
  handleSaveSettings,
} from '@/redux/slice/setting.slice';
import { BlockStack, Box, Card, InlineStack, Link, RadioButton } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleAPIModal } from '../../Modal/GoogleAPIModal';

export const DeliveryRestriction = () => {
  const { t } = useTranslation('general_settings');
  const dispatch = useDispatch();
  const isOpenAddPostalCodeBanner = useSelector(isOpenAddPostalCodeBannerSelector);
  const isOpenNoValidPostalCodeBanner = useSelector(isOpenNoValidPostalCodeBannerSelector);
  const isOpenCustomRadiusBanner = useSelector(isOpenCustomRadiusBannerSelector);
  const generalSettings = useSelector(generalSettingSelector);
  const [isShowGoogleMapApiModal, setIsShowGoogleMapApiModal] = useState(false);
  const showBannerGGApi = useSelector(showBannerGGApiSelector);
  const [checkGoogleRoutesApiKey, { isFetching: isLoadingCheckGoogleRoutesApiKey }] = useLazyCheckGoogleRoutesApiKeyQuery();
  const [updateGeneralSetting, { isLoading: isLoadingGeneralSetting }] = useUpdateGeneralSettingMutation();
  const { refetch: refetchGeneralSettingData } = useGetGeneralSettingQuery({ shop: config.shop });
  const errorFields = useSelector(errorFieldsSelector);
  const generalSettingsBackup = useSelector(generalBackupSettingSelector);

  const isExistGoogleMapApiKey = useMemo(
    () => !isEmpty(generalSettingsBackup.googleRoutesApiKey),
    [generalSettingsBackup.googleRoutesApiKey],
  );

  const handleCheckGoogleMapApiKey = () => {
    checkGoogleRoutesApiKey({
      shop: config.shop,
      key: generalSettings.googleRoutesApiKey.trim(),
    })
      .unwrap()
      .then((res) => {
        if (res.data.status === GOOGLE_API_STATUS.INVALID) {
          dispatch(
            handleSetErrorFields({
              errorFields: {
                errorValidGGRoutesApi: true,
              },
              isValidRoutesAPIKey: false,
            }),
          );
          dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerWarningGoogleAPIDeliveryRestriction', value: true }));
        } else if (res.data.status === GOOGLE_API_STATUS.VALID) {
          dispatch(
            handleSetErrorFields({
              errorFields: {
                errorValidGGRoutesApi: false,
              },
              isValidRoutesAPIKey: true,
            }),
          );
          dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerValidGoogleAPIDeliveryRestriction', value: true }));
          updateGeneralSetting({
            shop: config.shop,
            googleRoutesApiKey: generalSettings.googleRoutesApiKey,
            deliveryRestrictionType: DELIVERY_RESTRICTION_CHOICE.CUSTOM,
          })
            .unwrap()
            .then(() => {
              openToast(dispatch);
              dispatch(handleSaveBackupSettings({ googleRoutesApiKey: generalSettings.googleRoutesApiKey }));
              refetchGeneralSettingData();
            })
            .then(() => setIsShowGoogleMapApiModal(false));
        }
      });
  };

  const handleChangeGoogleMapApiKey = (value: string) => {
    dispatch(
      handleSetErrorFields({
        errorFields: {
          errorValidGGRoutesApi: false,
        },
        isValidRoutesAPIKey: false,
      }),
    );
    dispatch(handleSaveSettings({ googleRoutesApiKey: value }));
  };

  const handleDismissGGApiValid = () => {
    dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerValidGoogleAPIDeliveryRestriction', value: false }));
  };

  const handleDismissGGWarning = () => {
    dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerWarningGoogleAPIDeliveryRestriction', value: false }));
  };

  return (
    <Card padding={'300'}>
      <BlockStack gap={'300'}>
        <Box>
          <InlineStack align="space-between">
            <BoldText>{t('sections.deliveryValidation.deliveryRestriction.title')}</BoldText>
            {isExistGoogleMapApiKey && generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.CUSTOM && (
              <BoldSmallText>
                <Link removeUnderline onClick={() => setIsShowGoogleMapApiModal(true)}>
                  {t('sections.bannerGoogleAPI.ggApiLink')}
                </Link>
              </BoldSmallText>
            )}
          </InlineStack>
          <RegularText tone="subdued">{t('sections.deliveryValidation.deliveryRestriction.helpText')}</RegularText>
        </Box>
        {generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.POSTAL_CODE_MATCH && (
          <InformationBanner
            isVisible={isOpenAddPostalCodeBanner}
            onDismiss={() =>
              dispatch(
                handleVisibleBanner({
                  key: 'isOpenAddPostalCodeBanner',
                  value: false,
                }),
              )
            }
          >
            <BoldSmallText>{t('sections.deliveryValidation.deliveryRestriction.postalCodeBanner.title')}</BoldSmallText>
            <RegularText>{t('sections.deliveryValidation.deliveryRestriction.postalCodeBanner.desc')}</RegularText>
          </InformationBanner>
        )}
        {generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.CUSTOM && (
          <InformationBanner
            isVisible={isOpenCustomRadiusBanner}
            onDismiss={() =>
              dispatch(
                handleVisibleBanner({
                  key: 'isOpenCustomRadiusBanner',
                  value: false,
                }),
              )
            }
          >
            <BoldSmallText>{t('sections.deliveryValidation.deliveryRestriction.custom.title')}</BoldSmallText>
            <RegularText>{t('sections.deliveryValidation.deliveryRestriction.custom.desc')}</RegularText>
          </InformationBanner>
        )}
        {generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.NO_VALID && (
          <InformationBanner
            isVisible={isOpenNoValidPostalCodeBanner}
            onDismiss={() =>
              dispatch(
                handleVisibleBanner({
                  key: 'isOpenNoValidPostalCodeBanner',
                  value: false,
                }),
              )
            }
          >
            <BoldSmallText>{t('sections.deliveryValidation.deliveryRestriction.noValidBanner.title')}</BoldSmallText>
            <RegularText>{t('sections.deliveryValidation.deliveryRestriction.noValidBanner.desc')}</RegularText>
          </InformationBanner>
        )}
        <Card padding={'300'}>
          <BlockStack>
            <RadioButton
              label={t('sections.deliveryValidation.deliveryRestriction.choice.postalCode')}
              checked={generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.POSTAL_CODE_MATCH}
              id="delivery-restrict-postal-code"
              name="delivery-restrict-postal-code"
              onChange={() =>
                dispatch(handleSaveSettings({ deliveryRestrictionType: DELIVERY_RESTRICTION_CHOICE.POSTAL_CODE_MATCH }))
              }
            />
            <RadioButton
              label={t('sections.deliveryValidation.deliveryRestriction.choice.customer')}
              checked={generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.CUSTOM}
              id="delivery-restrict-custom"
              name="delivery-restrict-custom"
              onChange={() => {
                if (isExistGoogleMapApiKey) {
                  dispatch(handleSaveSettings({ deliveryRestrictionType: DELIVERY_RESTRICTION_CHOICE.CUSTOM }));
                } else setIsShowGoogleMapApiModal(true);
              }}
            />
            {generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.CUSTOM && (
              <StyledPadding paddingLeft="24px">
                <BlockStack>
                  <RegularText></RegularText>
                  <RadioButton
                    label={t('sections.deliveryValidation.deliveryRestriction.customer.km')}
                    checked={generalSettings.deliveryDistanceUnit === DELIVERY_RESTRICTION_CUSTOM.KM}
                    id="delivery-restrict-custom-km"
                    name="delivery-restrict-custom-km"
                    onChange={() => dispatch(handleSaveSettings({ deliveryDistanceUnit: DELIVERY_RESTRICTION_CUSTOM.KM }))}
                  />
                  <RadioButton
                    label={t('sections.deliveryValidation.deliveryRestriction.customer.mi')}
                    checked={generalSettings.deliveryDistanceUnit === DELIVERY_RESTRICTION_CUSTOM.MI}
                    id="delivery-restrict-custom-mi"
                    name="delivery-restrict-custom-mi"
                    onChange={() => dispatch(handleSaveSettings({ deliveryDistanceUnit: DELIVERY_RESTRICTION_CUSTOM.MI }))}
                  />
                </BlockStack>
              </StyledPadding>
            )}
            <RadioButton
              label={t('sections.deliveryValidation.deliveryRestriction.choice.noValid')}
              checked={generalSettings.deliveryRestrictionType === DELIVERY_RESTRICTION_CHOICE.NO_VALID}
              id="delivery-restrict-no-valid"
              name="delivery-restrict-no-valid"
              onChange={() => dispatch(handleSaveSettings({ deliveryRestrictionType: DELIVERY_RESTRICTION_CHOICE.NO_VALID }))}
            />
          </BlockStack>
        </Card>
        <Box>
          {t('sections.deliveryValidation.deliveryRestriction.instruction')}{' '}
          <Link onClick={() => handleSendMessageCrisp(t('sections.deliveryValidation.deliveryRestriction.instructionContent'))}>
            {t('sections.deliveryValidation.deliveryRestriction.instructionLink')}
          </Link>
        </Box>
      </BlockStack>
      <GoogleAPIModal
        open={isShowGoogleMapApiModal}
        googleMapApiKey={generalSettings.googleRoutesApiKey}
        onChange={(value) => handleChangeGoogleMapApiKey(value)}
        onSubmit={handleCheckGoogleMapApiKey}
        onCancel={() => {
          if (!(isLoadingCheckGoogleRoutesApiKey || isLoadingGeneralSetting)) {
            dispatch(handleSaveSettings({ googleRoutesApiKey: generalSettingsBackup.googleRoutesApiKey }));
            dispatch(handleResetErrorFields());
            setIsShowGoogleMapApiModal(false);
          }
        }}
        isVisible={showBannerGGApi.isShowBannerInfoGoogleAPIDeliveryRestriction}
        loading={isLoadingCheckGoogleRoutesApiKey || isLoadingGeneralSetting}
        onDismissInfo={() =>
          dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerInfoGoogleAPIDeliveryRestriction', value: false }))
        }
        handleDismissSuccess={handleDismissGGApiValid}
        handleDismissWarning={handleDismissGGWarning}
        isErrorShow={errorFields.errorFields?.errorValidGGRoutesApi}
        isSuccessShow={errorFields.isValidRoutesAPIKey && showBannerGGApi.isShowBannerValidGoogleAPIDeliveryRestriction}
        isWarningBannerShow={showBannerGGApi.isShowBannerWarningGoogleAPIDeliveryRestriction}
      />
    </Card>
  );
};
